import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import VideoUploader from '@/admin/VideoUploader';
import { TextField, NumberField, Toggle, Section, Row, SaveBar, PillSelect } from '@/admin/AdminUI';
import { HERO_ICON_KEYS } from '@/components/sections/HeroSection';

const ENTRANCE_SPEEDS = [
  { value: 'slow', label: 'Slow' },
  { value: 'normal', label: 'Normal' },
  { value: 'fast', label: 'Fast' },
  { value: 'off', label: 'Off' },
];

export default function HeroTab() {
  const [hero, setHero] = useState(null);
  // v3.18 — second sub-state slice for site_settings.global_ctas. The
  // Hero tab now offers a convenience edit point for the third hero CTA
  // (label + URL) which are stored in global_ctas (not the hero
  // namespace) because they're shared with the Partners/Gallery section.
  // Loaded from the same /admin/settings response as `hero`, saved via
  // the dedicated global_ctas PUT endpoint alongside the hero PUT.
  const [globalCtas, setGlobalCtas] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((r) => {
      setHero(r.data?.hero || {});
      setGlobalCtas(r.data?.global_ctas || {});
    });
  }, []);

  if (!hero) return <div className="font-body text-ink-muted">Loading…</div>;

  const set = (k, v) => setHero({ ...hero, [k]: v });
  // v3.18 — global_ctas mutator (mirrors `set` for the hero namespace).
  const setGc = (k, v) => setGlobalCtas({ ...globalCtas, [k]: v });
  const setStat = (idx, k, v) => {
    const stats = [...(hero.stats || [])];
    stats[idx] = { ...stats[idx], [k]: v };
    set('stats', stats);
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      // v3.18 — Persist hero + global_ctas in parallel. The global_ctas
      // PUT is the same v3.17 endpoint used by Site Settings → Global
      // CTAs, so both admin surfaces target the same DB fields.
      await Promise.all([
        api.put('/admin/settings/hero', hero),
        api.put('/admin/settings/global-ctas', globalCtas),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  const bgType = hero.background_type || 'image';
  return (
    <div>
      <Section title="Hero Background">
        <PillSelect label="Background Type" value={bgType} options={['image', 'video']} onChange={(v) => set('background_type', v)} testid="hero-bg-type" />
        <ImageUploader value={hero.poster_url || hero.image_url} onChange={(v) => set('poster_url', v)} label="Poster Image (also used as fallback)" testid="hero-poster" />
        {bgType === 'video' && (
          <div className="mt-4">
            <VideoUploader value={hero.video_url} onChange={(v) => set('video_url', v)} label="Hero Video (MP4)" />
          </div>
        )}
        <Row>
          <NumberField label="Overlay Opacity (0–0.85)" value={hero.overlay_opacity ?? 0.55} min={0} max={0.85} step={0.05} onCommit={(n) => set('overlay_opacity', n)} />
          <Toggle label="Auto-play on mobile" value={hero.autoplay_mobile !== false} onChange={(v) => set('autoplay_mobile', v)} />
        </Row>
        <Toggle label="Hero entrance animation enabled" value={hero.entrance_enabled !== false} onChange={(v) => set('entrance_enabled', v)} />
        <PillSelect label="Entrance Sequence Speed" value={hero.entrance_speed || 'normal'} options={ENTRANCE_SPEEDS} onChange={(v) => set('entrance_speed', v)} testid="hero-entrance-speed" />
      </Section>

      <Section title="Hero Copy">
        <Row>
          <TextField label="Location" value={hero.location} onChange={(v) => set('location', v)} />
          <TextField label="Date" value={hero.date} onChange={(v) => set('date', v)} />
        </Row>
        <Row>
          <TextField label="Headline Line 1" value={hero.headline_line1} onChange={(v) => set('headline_line1', v)} />
          <TextField label="Headline Line 2" value={hero.headline_line2} onChange={(v) => set('headline_line2', v)} />
        </Row>
        <Row>
          <Toggle label="Line 1 in Orange" value={hero.headline_line1_orange} onChange={(v) => set('headline_line1_orange', v)} />
          <Toggle label="Line 2 in Orange" value={hero.headline_line2_orange} onChange={(v) => set('headline_line2_orange', v)} />
        </Row>
        <Row>
          <TextField label="Sub-headline Words (comma-separated)" value={(hero.subhead_words || []).join(', ')} onChange={(v) => set('subhead_words', v.split(',').map((s) => s.trim()).filter(Boolean))} />
          <NumberField label="Orange Word Index (0-based)" value={hero.subhead_orange_index ?? 2} min={0} max={999} step={1} onCommit={(n) => set('subhead_orange_index', n)} />
        </Row>
      </Section>

      <Section title="Call-to-Action Buttons">
        <Row>
          <TextField label="Primary CTA Label" value={hero.cta_primary_label} onChange={(v) => set('cta_primary_label', v)} />
          <TextField label="Primary CTA URL" value={hero.cta_primary_url} onChange={(v) => set('cta_primary_url', v)} />
        </Row>
        {/* v3.9 — BOOK ROOMS CTA (Bellagio reservation). Lives in
            site_settings.hero alongside the primary + speakers CTAs
            because the URL is hero-specific (not shared like the
            sponsorship URL is). Visibility toggle hides the button
            without deleting the URL. */}
        <Row>
          <TextField
            label="Book Rooms Label"
            value={hero.cta_book_rooms_label}
            onChange={(v) => set('cta_book_rooms_label', v)}
            placeholder="Book Rooms"
            testid="hero-cta-book-rooms-label"
          />
          <TextField
            label="Book Rooms URL"
            value={hero.cta_book_rooms_url}
            onChange={(v) => set('cta_book_rooms_url', v)}
            placeholder="https://www.guestreservations.com/bellagio-las-vegas/booking"
            testid="hero-cta-book-rooms-url"
          />
        </Row>
        <Toggle
          label="Show Book Rooms button"
          value={hero.cta_book_rooms_visible !== false}
          onChange={(v) => set('cta_book_rooms_visible', v)}
          testid="hero-cta-book-rooms-visible"
        />
        <Row>
          <TextField label="View Speakers Label" value={hero.cta_speakers_label} onChange={(v) => set('cta_speakers_label', v)} />
          <TextField label="View Speakers URL (e.g. #speakers)" value={hero.cta_speakers_url} onChange={(v) => set('cta_speakers_url', v)} />
        </Row>
        {/* v3.18 — Third CTA pair. Wired to site_settings.global_ctas
            (NOT site_settings.hero) because the same URL is also
            consumed by the Partners/Gallery section. Single source of
            truth in the DB; this row is a convenience edit point
            duplicated from Site Settings → Global CTAs. */}
        <Row>
          <TextField
            label="Sponsorship Opportunities Label"
            value={globalCtas.sponsorship_label}
            onChange={(v) => setGc('sponsorship_label', v)}
            placeholder="Sponsorship Opportunities"
            testid="hero-cta-sponsorship-label"
          />
          <TextField
            label="Sponsorship Opportunities URL (e.g. mailto: or https://)"
            value={globalCtas.sponsorship_url}
            onChange={(v) => setGc('sponsorship_url', v)}
            placeholder="mailto:tiffanys@sei-con.org or https://..."
            testid="hero-cta-sponsorship-url"
          />
        </Row>
        <p className="font-body text-[11px] text-ink-soft mt-1">Shared with Site Settings → Global CTAs. Editing here also updates the Partners/Gallery section&apos;s sponsorship link.</p>
        {/* v3.11 — SCHEDULE CTA (outlined secondary, fifth row position).
            Lives in site_settings.hero alongside the other hero CTAs
            because the URL is hero-specific (links to the Swoogo public
            schedule page). Visibility toggle hides the button without
            deleting the URL. */}
        <Row>
          <TextField
            label="Schedule Label"
            value={hero.cta_schedule_label}
            onChange={(v) => set('cta_schedule_label', v)}
            placeholder="Schedule"
            testid="hero-cta-schedule-label"
          />
          <TextField
            label="Schedule URL"
            value={hero.cta_schedule_url}
            onChange={(v) => set('cta_schedule_url', v)}
            placeholder="https://circle.swoogo.com/sei_coniii/10027978"
            testid="hero-cta-schedule-url"
          />
        </Row>
        <Toggle
          label="Show Schedule button"
          value={hero.cta_schedule_visible !== false}
          onChange={(v) => set('cta_schedule_visible', v)}
          testid="hero-cta-schedule-visible"
        />
      </Section>

      <Section title="Stats (5 values)">
        <div className="space-y-4">
          {(hero.stats || []).map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 border border-border-line rounded-sm">
              <PillSelect label="Icon" value={s.icon} options={HERO_ICON_KEYS} onChange={(v) => setStat(i, 'icon', v)} testid={`hero-stat-icon-${i}`} />
              <TextField label="Number" value={s.number} onChange={(v) => setStat(i, 'number', v)} />
              <TextField label="Label" value={s.label} onChange={(v) => setStat(i, 'label', v)} />
            </div>
          ))}
        </div>
      </Section>

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}
